import { useEffect, useMemo, useState } from "react";
import { useGetBundlePdpQuery } from "../../shared/api/backendApi";
import { useAddItemsToCartMutation } from "../../shared/api/shopifyCartApi";
import { BundlePdpLayout } from "./components/BundlePdpLayout";
import {
  getInitialVariantId,
  getSelectedVariant,
  getVariantPrice,
} from "../../shared/pdp/utils/pricing";
import { readHalmParametersQueryString } from "../../shared/pdp/utils/halmParameters";
import { PdpNavigationSteps } from "../../shared/pdp/components/PdpNavigationSteps";
import {
  getCurrentNavigationStep,
  navigateToNextPdpStepOrCart,
  openCartDrawerIfReturningFromCheckout,
} from "../../shared/pdp/utils/navigationSteps";
import { toShopifyNumericId } from "../../shared/pdp/utils/shopifyIds";
import { LoadingSpinner } from "../../shared/pdp/components/LoadingSpinner";
import type { BundlePdpConfig } from "./types";
import "./styles.css";
import { PortableText } from "../../shared/pdp/components/PortableText";
import { getThemeAssetUrl } from "../../shared/pdp/utils/themeAssets";

type BundlePdpAppProps = {
  config: BundlePdpConfig;
};

const BUNDLE_COMPARE_AT_PRICE_PROPERTY = "_bundle_compare_at_price_cents";

export function BundlePdpApp({ config }: BundlePdpAppProps) {
  const queryString = useMemo(readHalmParametersQueryString, []);
  const objectionReasonsPortalTarget = useMemo(
    () =>
      document.querySelector<HTMLElement>(
        "[data-bundle-objection-reasons-root]",
      ),
    [],
  );
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const coachMatiasImageUrl = getThemeAssetUrl("coach-matias.png");

  const {
    data: bundle,
    isError,
    isLoading,
  } = useGetBundlePdpQuery({
    productId: config.productId,
    queryString,
  });
  const [addItemsToCart, addItemsToCartState] = useAddItemsToCartMutation();

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        openCartDrawerIfReturningFromCheckout();
      }
    };

    openCartDrawerIfReturningFromCheckout();
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (!bundle || selectedVariantId) return;

    setSelectedVariantId(getInitialVariantId(bundle));
  }, [bundle, selectedVariantId]);

  const selectedVariant = bundle
    ? getSelectedVariant(bundle, selectedVariantId)
    : null;
  const price = selectedVariant
    ? getVariantPrice(selectedVariant)
    : {
        price: 0,
        compareAtPrice: 0,
        discountPercentage: 0,
      };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;

    setStatusMessage("");

    try {
      const addToCartResponse = await addItemsToCart({
        items: [
          {
            id: toShopifyNumericId(selectedVariant.variantId),
            quantity: 1,
            ...(selectedVariant.compareAtPriceCents
              ? {
                  properties: {
                    [BUNDLE_COMPARE_AT_PRICE_PROPERTY]: String(
                      selectedVariant.compareAtPriceCents,
                    ),
                  },
                }
              : {}),
          },
        ],
        sections: ["cart-drawer", "cart-icon-bubble"],
        sections_url: window.location.pathname,
      }).unwrap();
      navigateToNextPdpStepOrCart(bundle?.navigationSteps, {
        cartSections: addToCartResponse.sections,
      });
    } catch {
      setStatusMessage("Der Artikel konnte nicht hinzugefügt werden.");
    }
  };
  const handleSkip = () => {
    navigateToNextPdpStepOrCart(bundle?.navigationSteps);
  };
  const isSkipVisible =
    getCurrentNavigationStep(bundle?.navigationSteps)?.isSkippable === true;

  return (
    <section
      className="react-pdp bundle-pdp"
      aria-labelledby="react-pdp-heading"
    >
      <div className="global-container">
        {isLoading ? (
          <div
            className="bundle-pdp__loading-screen"
            role="status"
            aria-live="polite"
            aria-label="Pflegeset wird geladen..."
          >
            <LoadingSpinner className="bundle-pdp__loading-spinner" />
          </div>
        ) : null}

        {isError ? (
          <div className="react-pdp__content">
            <p className="react-pdp__status">
              Das Pflegeset konnte nicht geladen werden.
            </p>
          </div>
        ) : null}

        {!isLoading && !isError && (!bundle || !selectedVariant) ? (
          <div className="react-pdp__content">
            <p className="react-pdp__status">
              Aktuell ist kein passendes Produkt verfügbar.
            </p>
          </div>
        ) : null}

        {!isLoading && !isError && bundle && selectedVariant ? (
          <div className="react-pdp__page-header-and-content">
            <div className="react-pdp__page-header">
              <PdpNavigationSteps navigationSteps={bundle.navigationSteps} />
              <div
                className="react-pdp__onetime-heading-wrap"
                id="react-pdp-heading"
              >
                <PortableText
                  className="react-pdp__onetime-heading"
                  emphasisVariant="accent"
                  value={bundle.heading}
                />
                <div className="react-pdp__onetime-coach-image-wrapper">
                  <img
                    alt=""
                    className="react-pdp__onetime-coach-image"
                    src={coachMatiasImageUrl}
                  />
                </div>
              </div>
            </div>
            <BundlePdpLayout
              bundle={bundle}
              compareAtPrice={price.compareAtPrice}
              discountPercentage={price.discountPercentage}
              isAddingToCart={addItemsToCartState.isLoading}
              isSkipVisible={isSkipVisible}
              objectionReasonsPortalTarget={objectionReasonsPortalTarget}
              onAddToCart={handleAddToCart}
              onSkip={handleSkip}
              onVariantChange={setSelectedVariantId}
              price={price.price}
              selectedVariantId={selectedVariant.variantId}
              statusMessage={statusMessage}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
